import type {
  DataGridPremiumProps,
  GridCellParams,
  GridColDef,
  GridGroupingColDefOverride,
  GridRowSelectionModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { DataGridPremium, isAutogeneratedRow, useKeepGroupedColumnsHidden } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import DataGridGroupHeader from "../grid/DataGridGroupHeader";
import InlineLoader from "../inlineLoader/InlineLoader";
import ScrollableFlexContainer from "../ScrollableFlexContainer";

interface Props {
  apiRef: NonNullable<DataGridPremiumProps["apiRef"]>;
  isLoading: boolean;
  rows: GridValidRowModel[];
  onValueSelect: (selection: GridRowSelectionModel) => void;
  selectedValueIds: number[];
  columns: GridColDef[];
  keyFieldName: string;
  groupByField: { fieldName: string } | undefined;
  preserveOrder?: boolean;
  singleSelect: boolean;
}

const SelectParameterGrid = ({
  isLoading,
  rows,
  onValueSelect,
  selectedValueIds,
  columns,
  keyFieldName,
  groupByField,
  preserveOrder = false,
  singleSelect,
  apiRef,
}: Props) => {
  const [rowsSelection, setRowsSelection] = useState<GridRowSelectionModel>(() => ({
    type: "include",
    ids: new Set(selectedValueIds),
  }));

  const handleRowSelectionModelChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      setRowsSelection(newSelection);
      onValueSelect(newSelection);
    },
    [onValueSelect]
  );

  const groupColumnDef = useMemo((): GridGroupingColDefOverride | undefined => {
    return {
      hideDescendantCount: true,
      headerName: keyFieldName,
      sortable: true,
      aggregable: false,
      hideable: false,
      pinnable: false,
      filterable: false,
      flex: 1,
      leafField: keyFieldName,
      renderHeader: (params) => <DataGridGroupHeader params={params} />,
      colSpan: (_: GridCellParams, row) => {
        if (isAutogeneratedRow(row)) {
          return 2;
        }
        return 1;
      },
      groupingValueGetter: (params) => {
        return params ?? "Memo";
      },
    };
  }, [keyFieldName]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      sorting: { sortModel: preserveOrder ? [] : [{ field: keyFieldName, sort: "asc" }] },
      columns: groupByField && {
        columnVisibilityModel: { [keyFieldName]: false },
      },
      rowGrouping: groupByField && {
        model: [groupByField?.fieldName ?? ""],
      },
    },
  });

  return (
    <ScrollableFlexContainer>
      <DataGridPremium
        key={preserveOrder ? "preserve-order" : "sortable"}
        slots={{
          loadingOverlay: InlineLoader,
        }}
        checkboxSelection={!singleSelect}
        disableMultipleRowSelection={singleSelect}
        hideFooter
        rowHeight={24}
        columnHeaderHeight={30}
        apiRef={apiRef}
        loading={isLoading}
        rows={rows}
        columns={columns}
        rowSelectionModel={rowsSelection}
        groupingColDef={groupColumnDef}
        initialState={initialState}
        keepNonExistentRowsSelected={true}
        rowSelectionPropagation={{ descendants: true, parents: true }}
        disableColumnMenu
        disableRowSelectionExcludeModel
        isGroupExpandedByDefault={isGroupExpandedByDefault}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        sx={(t) => ({
          minHeight: "255px",
          "--DataGrid-rowBorderColor": t.palette.divider,
          border: "1px solid " + t.palette.divider,
          ".MuiDataGrid-columnHeaders": {
            borderBottom: "none",
          },
          ".MuiDataGrid-cell:focus-within": {
            outline: "unset",
          },
          ".MuiDataGrid-columnHeader:focus-within": {
            outline: "unset",
          },
          ".MuiDataGrid-row": {
            cursor: singleSelect ? "pointer" : undefined,
            ".MuiDataGrid-cell": {
              borderTop: "none",
              ".MuiDataGrid-groupingCriteriaCell": {
                fontWeight: "500",
                ".MuiDataGrid-groupingCriteriaCellToggle": {
                  marginRight: "4px",
                },
              },
            },
          },
          ".MuiButtonBase-root.MuiCheckbox-root": {
            p: 0.75,
          },
          ".MuiDataGrid-scrollbarFiller.MuiDataGrid-scrollbarFiller--borderTop": {
            border: "none",
          },
        })}
      />
    </ScrollableFlexContainer>
  );
};

export default SelectParameterGrid;

const isGroupExpandedByDefault = () => true;
